import React from "react";
import { MapPin, MapPinOff, AlertTriangle, Loader2 } from "lucide-react";
import type { GpsState } from "./useGps";

// Compact GPS status indicator. Shows a loading spinner while we wait for a
// first fix, then the accuracy in metres once locked. Tapping it refreshes.

export function GpsIndicator({ gps, compact = false }: { gps: GpsState; compact?: boolean }) {
  const { status, fix, refresh } = gps;

  if (status === "requesting" || (status === "idle" && !fix)) {
    return (
      <button
        onClick={refresh}
        className="inline-flex items-center gap-1.5 text-[11px] text-text-muted font-mono"
      >
        <Loader2 size={11} className="animate-spin" />
        {compact ? "GPS…" : "Getting location…"}
      </button>
    );
  }

  if (status === "denied" || status === "unavailable" || status === "timeout") {
    return (
      <button
        onClick={refresh}
        className="inline-flex items-center gap-1.5 text-[11px] text-warn font-mono"
        title="Tap to retry"
      >
        <MapPinOff size={11} />
        {compact ? "no GPS" : gps.error ?? "No GPS"}
      </button>
    );
  }

  if (!fix) return null;
  const acc = Math.round(fix.accuracy_m);
  const quality = acc <= 15 ? "text-ok" : acc <= 50 ? "text-warn" : "text-danger";

  return (
    <button
      onClick={refresh}
      className={`inline-flex items-center gap-1.5 text-[11px] font-mono ${quality}`}
      title={`${fix.latitude.toFixed(5)}, ${fix.longitude.toFixed(5)} · tap to refine`}
    >
      <MapPin size={11} />
      {compact ? `±${acc}m` : `GPS locked · ±${acc}m`}
    </button>
  );
}
